import React, { useState } from "react";
import axios from "axios";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistForm({
  interest = "curriculum",
  testIdPrefix = "waitlist",
  heading,
  subheading,
  surface = "card", // 'card' | 'plain'
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError(t("waitlist.emailRequired"));
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/waitlist`, {
        name: name || null,
        email,
        interest,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          t("waitlist.genericError")
      );
    }
  };

  if (status === "success") {
    return (
      <div
        className="surface elev-1 p-8 md:p-10 border-[#B89B5E]/30"
        data-testid={`${testIdPrefix}-success`}
      >
        <div className="flex items-center gap-3 text-[#9A7E47] mb-4">
          <Check size={18} />
          <span className="eyebrow">{t("waitlist.successEyebrow")}</span>
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] leading-tight">
          {t("waitlist.successTitle")}
        </h3>
        <p className="text-[#4A4A4A] mt-4 leading-relaxed">
          {t("waitlist.successBody")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={surface === "card" ? "surface elev-1 p-8 md:p-12" : ""}
      data-testid={`${testIdPrefix}-form`}
    >
      {heading && (
        <h3 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight leading-tight">
          {heading}
        </h3>
      )}
      {subheading && (
        <p className="text-[#4A4A4A] mt-4 max-w-md leading-relaxed">{subheading}</p>
      )}

      <div className={`${heading ? "mt-10" : ""} grid gap-6 md:grid-cols-2`}>
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
            {t("waitlist.nameLabel")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="field mt-2"
            placeholder={t("waitlist.namePh")}
            data-testid={`${testIdPrefix}-name-input`}
          />
        </div>
        <div>
          <label className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
            {t("waitlist.emailLabel")}
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field mt-2"
            placeholder={t("waitlist.emailPh")}
            data-testid={`${testIdPrefix}-email-input`}
          />
        </div>
      </div>

      {error && (
        <p className="text-red-600 text-sm mt-6" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}

      <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
        <button
          type="submit"
          className="btn-ink"
          disabled={status === "loading"}
          data-testid={`${testIdPrefix}-submit`}
        >
          {status === "loading" ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {t("waitlist.sending")}
            </>
          ) : (
            <>
              {t("waitlist.submit")} <ArrowRight size={16} />
            </>
          )}
        </button>
        <p className="text-[#707070] text-xs tracking-wider uppercase">
          {t("waitlist.nameHint")}
        </p>
      </div>
    </form>
  );
}
